import {
  CopyTask,
  Icon,
  NewTask,
  getTranslatedText,
  BackgroundIcon,
  AccessControl
} from '@nile/components';
import { Medal, CalendarCheck } from '@styled-icons/fa-solid';
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import {
  ActionList,
  Popover,
  ButtonGroup,
  Button
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';
import { constructTaskObject } from '@nile/projects/src/Utils';
import { FormattedMessage, injectIntl } from 'react-intl';

const SecondaryPlainButton = styled.div`
  .Superops-Button--plainSecondary {
    color: #000 !important;
  }
`;

const CreateMilestoneTaskCard = ({
  intl,
  addMilestone,
  handleOtherTasksAddition,
  handleOtherTaskTemplateAddition,
  taskTemplates,
  isActionList,
  module,
  hideSectionsInTaskForm = [],
  sendAllKeysInTemplate,
  isTemplate,
  getSelectedTaskNameList
}) => {
  const [sheetActive, setSheetActive] = useState(false);
  const [active, setActive] = useState(false);

  const toggleActive = useCallback(() => setActive((active) => !active), []);

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };

  const handleTaskAddition = (type, task) => {
    const taskObj = constructTaskObject(task, hideSectionsInTaskForm);
    handleOtherTasksAddition(taskObj);
    setSheetActive(!sheetActive);
    toggleActive();
  };

  const filterAttrs = (obj) => {
    const { name, ...rest } = obj;
    return rest;
  };

  const updateContext = (type, updatedTasks) => {
    if (type === 'taskTemplateList') {
      const updatedTaskList = updatedTasks.map((taskTemp) => {
        if (sendAllKeysInTemplate) {
          return {
            ...constructTaskObject(taskTemp, hideSectionsInTaskForm),
            taskTemplateId: taskTemp.taskTemplateId
          };
        }
        const { taskTemplateId, ...rest } = taskTemp;
        if (rest.technician) {
          rest.technician = filterAttrs(rest.technician);
        }
        return rest;
      });
      handleOtherTaskTemplateAddition(updatedTaskList);
      if (isActionList) toggleActive();
    }
  };

  const context = {
    value: {
      addTaskTemplateList: [],
      removeTaskTemplateList: [],
      taskTemplateList: [],
      selectedTaskTemplates: []
    },
    updateContext: updateContext
  };

  const activator = (
    <Button icon={<SVGIcon name="Actions" />} onClick={toggleActive} disclosure>
      <FormattedMessage id="ticket.newProject.actions" />
    </Button>
  );

  const modifiedTaskList =
    taskTemplates?.getTaskTemplateList?.map((task) => ({
      ...task,
      id: task.taskTemplateId,
      name: task.title,
      label: task.title
    })) || [];

  const tasksList = getSelectedTaskNameList
    ? modifiedTaskList?.map((task) => ({
        ...task,
        disable: getSelectedTaskNameList().includes(task.title)
      })) || []
    : modifiedTaskList;

  if (isActionList) {
    return (
      <Popover active={active} activator={activator} onClose={() => {}}>
        <ActionList
          actionRole="menuitem"
          items={[
            {
              content: <FormattedMessage id="ticket.newProject.milestone" />,
              onAction: () => {
                addMilestone();
                toggleActive();
              },
              iconName: 'Milestone'
            },
            {
              content: (
                <SecondaryPlainButton>
                  <NewTask
                    addOrUpdateTechGroup={handleTaskAddition}
                    state={sheetActive}
                    toggleSheetActive={() => {
                      toggleSheetActive();
                      if (sheetActive) toggleActive();
                    }}
                    name={getTranslatedText(
                      intl,
                      'ticket.newProject.createTask'
                    )}
                    hideIcon
                    isText
                    module={module || 'PROJECT'}
                    hideSections={hideSectionsInTaskForm}
                    details={{ operation: 'CREATE' }}
                    isTemplate={isTemplate}
                  />
                </SecondaryPlainButton>
              ),
              iconName: 'Tasks'
            },
            {
              content: (
                <SecondaryPlainButton>
                  <CopyTask
                    tasksList={tasksList}
                    context={context}
                    name={getTranslatedText(intl, 'ticket.newProject.tempTask')}
                    sendAll={sendAllKeysInTemplate}
                    usePropTemplates={!!getSelectedTaskNameList}
                    hideSectionsInTask={hideSectionsInTaskForm}
                    hideIcon
                    isText
                  />
                </SecondaryPlainButton>
              ),
              iconName: 'Tasks'
            }
          ]}
        />
      </Popover>
    );
  }

  return (
    <div className="fx">
      <ButtonGroup>
        <div data-qa="createMilestone">
          <Button
            onClick={addMilestone}
            icon={
              <div className="opacity5">
                <SVGIcon name="Milestone" color="#323232" />
              </div>
            }
          >
            <FormattedMessage id="ticket.newProject.milestone" />
          </Button>
        </div>
        <div data-qa="createTask">
          <NewTask
            addOrUpdateTechGroup={handleTaskAddition}
            state={sheetActive}
            toggleSheetActive={toggleSheetActive}
            name={getTranslatedText(intl, 'ticket.newProject.createTask')}
            hideIcon
            module={module || 'PROJECT'}
            hideSections={hideSectionsInTaskForm}
            details={{ operation: 'CREATE' }}
            isTemplate={isTemplate}
            icon={
              <div className="opacity5">
                <SVGIcon name="Tasks" />
              </div>
            }
          />
        </div>
        <AccessControl
          roleToCheck="taskTemplate.view"
          render={
            <div data-qa="createTaskTemplate">
              <CopyTask
                tasksList={tasksList}
                context={context}
                name={getTranslatedText(intl, 'ticket.newProject.tempTask')}
                sendAll={sendAllKeysInTemplate}
                usePropTemplates={!!getSelectedTaskNameList}
                hideSectionsInTask={hideSectionsInTaskForm}
                icon={
                  <div className="opacity5">
                    <SVGIcon name="Tasks" />
                  </div>
                }
                hideIcon
              />
            </div>
          }
        />
      </ButtonGroup>
    </div>
  );
};

export default injectIntl(CreateMilestoneTaskCard);
